<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Educación</h1>
          <p class="text-blue-100 mt-1">Gestiona los artículos educativos para mascotas</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Artículos</p>
            <p class="text-2xl font-semibold text-gray-900">{{ educationItems?.length || 0 }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Con Video</p>
            <p class="text-2xl font-semibold text-gray-900">{{ educationItems?.filter(item => item.video).length || 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <!-- Search Filter -->
        <div class="w-full lg:flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
          <InputText
            v-model="globalFilter"
            placeholder="Buscar por título o descripción..."
            class="w-full"
          />
        </div>

        <!-- Animal Type Filter -->
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Tipo de animal</label>
          <select
            v-model="selectedAnimalTypeId"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-white"
          >
            <option :value="null">Todos</option>
            <option v-for="type in animalTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
          </select>
        </div>

        <!-- Add Button -->
        <div class="flex-shrink-0">
          <button
            @click="router.push('/education/create')"
            class="inline-flex items-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Agregar Artículo
          </button>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        :value="filteredEducationItems"
        :paginator="true"
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :loading="loading"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} a {last} de {totalRecords} artículos"
        responsiveLayout="stack"
        class="p-datatable-sm"
        tableStyle="table-layout: fixed; width: 100%;"
      >
        <template #empty>
          <div class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay artículos</h3>
            <p class="mt-1 text-sm text-gray-500">Comienza creando un nuevo artículo educativo.</p>
          </div>
        </template>

        <Column field="image" header="Imagen" sortable style="width: 80px; min-width: 80px;">
          <template #body="{ data }">
            <img
              v-if="data.image"
              :src="data.image"
              alt="Imagen educativa"
              class="w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
              @click="openImageModal(data.image, data.title)"
            />
            <span v-else class="text-gray-400">Sin imagen</span>
          </template>
        </Column>

        <Column field="title" header="Título" sortable style="width: 60%;">
          <template #body="{ data }">
            <div>
              <div class="text-sm font-medium text-gray-900">{{ data.title }}</div>
              <div class="text-sm text-gray-500">{{ data.description ? data.description.substring(0, 50) + '...' : 'Sin descripción' }}</div>
            </div>
          </template>
        </Column>

        <Column field="video" header="Video" sortable style="width: 100px; min-width: 100px;">
          <template #body="{ data }">
            <div v-if="data.video" class="relative">
              <video
                :src="data.video"
                class="w-24 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                @click="openVideoModal(data.video, data.title)"
                muted
              >
                Tu navegador no soporta el elemento video.
              </video>
              <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg class="w-6 h-6 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Sin video
            </span>
          </template>
        </Column>



        <Column header="Tipo de Animal" style="width: 140px; min-width: 120px;">
          <template #body="{ data }">
            <span v-if="getAnimalTypeName(data.animalTypeId, data.animalType)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="getAnimalTypeBadgeClass(data.animalTypeId, data.animalType)">
              {{ getAnimalTypeName(data.animalTypeId, data.animalType) }}
            </span>
            <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Todos
            </span>
          </template>
        </Column>

        <Column header="Acciones" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center space-x-2">
              <button
                @click="editEducation(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style="background-color: #1d4ed8; color: #fff"
                title="Editar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                @click="deleteEducation(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                style="background-color: #be123c; color: #fff"
                title="Eliminar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :show="showDeleteModal"
      title="Eliminar Artículo"
      :message="`¿Estás seguro de que deseas eliminar el artículo '${selectedEducation?.title}'? Esta acción no se puede deshacer.`"
      confirmText="Eliminar"
      confirmClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />

    <!-- Image Modal -->
    <ImageModal
      :show="showImageModal"
      :image-url="selectedImage"
      :image-alt="selectedImageAlt"
      @close="closeImageModal"
    />

    <VideoModal
      :show="showVideoModal"
      :video-url="selectedVideo"
      :title="selectedVideoTitle"
      @close="closeVideoModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import InputText from 'primevue/inputtext'
import type { Education } from '@/types'
import { educationService } from '@/services/educationService'
import { useNotifications } from '@/composables/useNotifications'
import { animalTypeService } from '@/services/animalTypeService'
import ConfirmModal from '@/components/ConfirmModal.vue'
import ImageModal from '@/components/ImageModal.vue'
import VideoModal from '@/components/VideoModal.vue'

const { showNotification } = useNotifications()
const router = useRouter()

const loading = ref(false)
const educationItems = ref<Education[]>([])
const selectedEducation = ref<Education | null>(null)
const showDeleteModal = ref(false)
const showImageModal = ref(false)
const selectedImage = ref('')
const selectedImageAlt = ref('')
const showVideoModal = ref(false)
const selectedVideo = ref('')
const selectedVideoTitle = ref('')
const globalFilter = ref('')
const animalTypes = ref<{ id: number; name: string }[]>([])
const selectedAnimalTypeId = ref<number | null>(null)

const filteredEducationItems = computed(() => {
  let items = educationItems.value
  
  if (selectedAnimalTypeId.value !== null) {
    items = items.filter(item => item.animalTypeId === selectedAnimalTypeId.value)
  }
  
  if (globalFilter.value) {
    const searchTerm = globalFilter.value.toLowerCase()
    items = items.filter(item => 
      item.title.toLowerCase().includes(searchTerm) ||
      (item.description && item.description.toLowerCase().includes(searchTerm))
    )
  }
  
  return items
})

const getAnimalTypeName = (animalTypeId: number | null | undefined, animalType?: { name?: string } | null) => {
  if (animalType?.name) {
    return animalType.name
  }

  if (animalTypeId === null || animalTypeId === undefined) {
    return null
  }

  const found = animalTypes.value.find(type => type.id === animalTypeId)
  return found?.name || null
}

const getAnimalTypeBadgeClass = (animalTypeId: number | null | undefined, animalType?: { name?: string } | null) => {
  const name = (getAnimalTypeName(animalTypeId, animalType) || '').toLowerCase()

  if (name.includes('perro')) return 'bg-blue-100 text-blue-800'
  if (name.includes('gato')) return 'bg-orange-100 text-orange-800'
  if (name.includes('ave')) return 'bg-sky-100 text-sky-800'
  if (name.includes('pez')) return 'bg-cyan-100 text-cyan-800'
  if (name.includes('conejo')) return 'bg-pink-100 text-pink-800'

  return 'bg-green-100 text-green-800'
}

const loadEducationItems = async () => {
  loading.value = true
  try {
    const response = await educationService.getAllEducation()
    educationItems.value = response.data || []
  } catch (error: any) {
    console.error('Error loading education items:', error)
    showNotification({ message: 'Error al cargar los artículos educativos', type: 'error' })
    educationItems.value = []
  } finally {
    loading.value = false
  }
}

const editEducation = (education: Education) => {
  router.push(`/education/${education.id}/edit`)
}

const deleteEducation = (education: Education) => {
  selectedEducation.value = education
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (!selectedEducation.value) return
  
  try {
    await educationService.deleteEducation(selectedEducation.value.id)
    await loadEducationItems()
    showNotification({ message: 'Artículo eliminado exitosamente', type: 'success' })
  } catch (error: any) {
    console.error('Error deleting education item:', error)
    showNotification({ message: 'Error al eliminar el artículo', type: 'error' })
  } finally {
    showDeleteModal.value = false
    selectedEducation.value = null
  }
}

const openVideoModal = (videoUrl: string, title: string) => {
  selectedVideo.value = videoUrl
  selectedVideoTitle.value = title
  showVideoModal.value = true
}

const closeVideoModal = () => {
  showVideoModal.value = false
  selectedVideo.value = ''
  selectedVideoTitle.value = ''
}

const openImageModal = (imageUrl: string, imageAlt: string) => {
  selectedImage.value = imageUrl
  selectedImageAlt.value = imageAlt
  showImageModal.value = true
}

const closeImageModal = () => {
  showImageModal.value = false
  selectedImage.value = ''
  selectedImageAlt.value = ''
}

onMounted(async () => {
  loadEducationItems()
  try {
    const res = await animalTypeService.getAllAnimalTypes()
    animalTypes.value = res.data || res
  } catch {}
})
</script>